import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ManagerFeedbackActions } from './manager-feedback.actions';
import { selectFilters } from './manager-feedback.selectors';
import { ManagerFeedbackApiService } from '@app/core/api/manager-feedback';
import { LoggerService } from '@app/core/infrastructure';
import { ToastNotificationService } from '@app/core/infrastructure';

@Injectable()
export class ManagerFeedbackEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly api = inject(ManagerFeedbackApiService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService).scope('ManagerFeedbackEffects');
  private readonly toastNotificationService = inject(ToastNotificationService);
  private readonly translate = inject(TranslateService);

  /**
   * Load feedback list with current filters and pagination params
   */
  loadFeedbackList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.loadFeedbackList),
      withLatestFrom(this.store.select(selectFilters)),
      switchMap(([{ params }, storeFilters]) => {
        const mergedParams = { ...storeFilters, ...params };
        return this.api.listFeedback(mergedParams).pipe(
          map(response => ManagerFeedbackActions.loadFeedbackListSuccess({
            items: response.items,
            total: response.total,
            page: response.page,
            size: response.size,
            pages: response.pages
          })),
          catchError(error => {
            this.logger.error('Load feedback list failed:', error);
            const errorMsg = error?.status === 403
              ? this.translate.instant('managerFeedback.accessDenied')
              : error?.error?.detail || error.message || 'Failed to load feedback list';
            return of(ManagerFeedbackActions.loadFeedbackListFailure({ error: errorMsg }));
          })
        );
      })
    )
  );

  /**
   * Load single feedback detail with notes
   */
  loadFeedbackDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.loadFeedbackDetail),
      switchMap(({ id }) =>
        this.api.getFeedbackDetail(id).pipe(
          map(feedback => ManagerFeedbackActions.loadFeedbackDetailSuccess({ feedback })),
          catchError(error => {
            this.logger.error('Load feedback detail failed:', error);
            const errorMsg = error?.status === 404
              ? 'Feedback not found'
              : error?.status === 403
                ? 'Access denied'
                : error?.error?.detail || error.message || 'Failed to load feedback detail';
            return of(ManagerFeedbackActions.loadFeedbackDetailFailure({ error: errorMsg }));
          })
        )
      )
    )
  );

  /**
   * Handle detail load failure — 404 redirects, others show toast
   */
  loadFeedbackDetailFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.loadFeedbackDetailFailure),
      tap(({ error }) => {
        if (error === 'Feedback not found') {
          this.toastNotificationService.showError(this.translate.instant('managerFeedback.notFound'));
          this.router.navigate(['/manager/feedback']);
        } else if (error === 'Access denied') {
          this.toastNotificationService.showError(this.translate.instant('managerFeedback.accessDenied'));
          this.router.navigate(['/manager/feedback']);
        } else {
          this.toastNotificationService.showError(this.translate.instant('managerFeedback.loadError'));
        }
      })
    ),
    { dispatch: false }
  );

  /**
   * Update feedback status
   */
  updateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.updateStatus),
      switchMap(({ id, status }) =>
        this.api.updateStatus(id, { status }).pipe(
          map(feedback => ManagerFeedbackActions.updateStatusSuccess({ feedback })),
          catchError(error => {
            this.logger.error('Update status failed:', error);
            const errorMsg = error?.status === 403
              ? this.translate.instant('managerFeedback.accessDenied')
              : error?.error?.detail || error.message || 'Failed to update status';
            return of(ManagerFeedbackActions.updateStatusFailure({ error: errorMsg }));
          })
        )
      )
    )
  );

  updateStatusSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.updateStatusSuccess),
      tap(() => {
        this.toastNotificationService.showSuccess(this.translate.instant('managerFeedback.statusUpdated'));
      })
    ),
    { dispatch: false }
  );

  /**
   * Add note to feedback
   */
  addNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.addNote),
      switchMap(({ id, text }) =>
        this.api.addNote(id, { note_text: text }).pipe(
          map(note => ManagerFeedbackActions.addNoteSuccess({ note })),
          catchError(error => {
            this.logger.error('Add note failed:', error);
            const errorMsg = error?.status === 403
              ? this.translate.instant('managerFeedback.accessDenied')
              : error?.error?.detail || error.message || 'Failed to add note';
            return of(ManagerFeedbackActions.addNoteFailure({ error: errorMsg }));
          })
        )
      )
    )
  );

  addNoteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.addNoteSuccess),
      tap(() => {
        this.toastNotificationService.showSuccess(this.translate.instant('managerFeedback.noteAdded'));
      })
    ),
    { dispatch: false }
  );

  /**
   * Send email to feedback user
   */
  sendEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.sendEmail),
      switchMap(({ id, subject, body }) =>
        this.api.sendEmail(id, { subject, body }).pipe(
          map(response => ManagerFeedbackActions.sendEmailSuccess({ response })),
          catchError(error => {
            this.logger.error('Send email failed:', error);
            const errorMsg = error?.status === 403
              ? this.translate.instant('managerFeedback.accessDenied')
              : error?.status === 502
                ? 'Email failed to send'
                : error?.status === 400
                  ? 'Cannot send email — no user email associated'
                  : error?.error?.detail || error.message || 'Failed to send email';
            return of(ManagerFeedbackActions.sendEmailFailure({ error: errorMsg }));
          })
        )
      )
    )
  );

  sendEmailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.sendEmailSuccess),
      tap(({ response }) => {
        this.toastNotificationService.showSuccess(
          this.translate.instant('managerFeedback.emailSent', { email: response.recipient_email })
        );
      })
    ),
    { dispatch: false }
  );

  sendEmailFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.sendEmailFailure),
      tap(({ error }) => {
        this.toastNotificationService.showError(error);
      })
    ),
    { dispatch: false }
  );

  /**
   * Delete feedback entry
   */
  deleteFeedback$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.deleteFeedback),
      switchMap(({ id }) =>
        this.api.deleteFeedback(id).pipe(
          map(() => ManagerFeedbackActions.deleteFeedbackSuccess({ id })),
          catchError(error => {
            this.logger.error('Delete feedback failed:', error);
            const errorMsg = error?.status === 404
              ? this.translate.instant('managerFeedback.notFound')
              : error?.status === 403
                ? this.translate.instant('managerFeedback.accessDenied')
                : error?.error?.detail || error.message || 'Failed to delete feedback';
            return of(ManagerFeedbackActions.deleteFeedbackFailure({ error: errorMsg }));
          })
        )
      )
    )
  );

  deleteFeedbackSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.deleteFeedbackSuccess),
      tap(() => {
        this.toastNotificationService.showSuccess(this.translate.instant('managerFeedback.feedbackDeleted'));
        this.router.navigate(['/manager/feedback']);
      })
    ),
    { dispatch: false }
  );

  /**
   * Delete a single note
   */
  deleteNote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.deleteNote),
      switchMap(({ feedbackId, noteId }) =>
        this.api.deleteNote(feedbackId, noteId).pipe(
          map(() => ManagerFeedbackActions.deleteNoteSuccess({ feedbackId, noteId })),
          catchError(error => {
            this.logger.error('Delete note failed:', error);
            const errorMsg = error?.status === 404
              ? this.translate.instant('managerFeedback.noteNotFound')
              : error?.status === 403
                ? this.translate.instant('managerFeedback.accessDenied')
                : error?.error?.detail || error.message || 'Failed to delete note';
            return of(ManagerFeedbackActions.deleteNoteFailure({ error: errorMsg }));
          })
        )
      )
    )
  );

  deleteNoteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ManagerFeedbackActions.deleteNoteSuccess),
      tap(() => {
        this.toastNotificationService.showSuccess(this.translate.instant('managerFeedback.noteDeleted'));
      })
    ),
    { dispatch: false }
  );

  /**
   * Show error snackbar on general failures
   */
  showErrorSnackbar$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ManagerFeedbackActions.loadFeedbackListFailure,
        ManagerFeedbackActions.updateStatusFailure,
        ManagerFeedbackActions.addNoteFailure,
        ManagerFeedbackActions.deleteFeedbackFailure,
        ManagerFeedbackActions.deleteNoteFailure
      ),
      tap(({ error }) => {
        this.toastNotificationService.showError(error);
      })
    ),
    { dispatch: false }
  );
}
